import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Save, Trash2, Upload, UserX } from 'lucide-react';
import api, { extrairMensagemErro } from '../services/api';
import { PerfilStatus } from '../types/perfil';
import { arquivoParaImagemUpload, obterErroImagem, resolverUrlMidia } from '../components/perfilUtils';
import { useDialog } from '../components/common/DialogContext';

type Props = {
  tipo: 'tenant' | 'fornecedor';
};

type FormPerfil = {
  telefone: string;
  whatsapp: string;
  email_operacional: string;
  nif: string;
  provincia: string;
  municipio: string;
  endereco: string;
  referencia: string;
  contacto_responsavel: string;
};

const vazio: FormPerfil = {
  telefone: '',
  whatsapp: '',
  email_operacional: '',
  nif: '',
  provincia: '',
  municipio: '',
  endereco: '',
  referencia: '',
  contacto_responsavel: ''
};

const emailValido = (valor: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor);

const PerfilOperacional: React.FC<Props> = ({ tipo }) => {
  const [form, setForm] = useState<FormPerfil>(vazio);
  const [imagemUrl, setImagemUrl] = useState('');
  const [imagem, setImagem] = useState<File | null>(null);
  const [imagemPreviaUrl, setImagemPreviaUrl] = useState('');
  const [status, setStatus] = useState<PerfilStatus | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [solicitandoEliminacao, setSolicitandoEliminacao] = useState(false);
  const dialog = useDialog();
  const inputImagemRef = useRef<HTMLInputElement>(null);
  const montadoRef = useRef(true);

  const endpoint = tipo === 'tenant' ? '/tenant/perfil' : '/fornecedores/perfil';

  const carregar = useCallback(async (signal?: AbortSignal) => {
    try {
      if (montadoRef.current) {
        setCarregando(true);
        setErro(null);
      }
      const resposta = await api.get(endpoint, { signal });
      if (!montadoRef.current || signal?.aborted) return;
      const dados = resposta.data.dados || {};
      setForm({
        telefone: dados.telefone_operacional || dados.telefone || '',
        whatsapp: dados.whatsapp || '',
        email_operacional: dados.email_operacional || dados.email || '',
        nif: dados.cnpj_nif || dados.nif || '',
        provincia: dados.provincia || '',
        municipio: dados.municipio || '',
        endereco: dados.endereco || '',
        referencia: dados.referencia || '',
        contacto_responsavel: dados.contacto_responsavel || ''
      });
      setImagemUrl(resolverUrlMidia(dados.logotipo_url || dados.foto_url));
      setStatus(resposta.data.status_perfil || null);
    } catch (falha: any) {
      if (montadoRef.current && !signal?.aborted) {
        setErro(extrairMensagemErro(falha, 'Erro ao carregar perfil operacional.'));
      }
    } finally {
      if (montadoRef.current && !signal?.aborted) setCarregando(false);
    }
  }, [endpoint]);

  useEffect(() => {
    montadoRef.current = true;
    const controller = new AbortController();
    carregar(controller.signal);
    return () => {
      montadoRef.current = false;
      controller.abort();
    };
  }, [carregar]);

  useEffect(() => {
    if (!imagem) {
      setImagemPreviaUrl('');
      return;
    }
    const urlTemporaria = URL.createObjectURL(imagem);
    setImagemPreviaUrl(urlTemporaria);
    return () => URL.revokeObjectURL(urlTemporaria);
  }, [imagem]);

  const atualizar = (campo: keyof FormPerfil, valor: string) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const validar = () => {
    if (form.email_operacional.trim() && !emailValido(form.email_operacional)) return 'Informe um e-mail operacional válido.';
    const erroImagem = obterErroImagem(imagem);
    if (erroImagem) return erroImagem;
    return null;
  };

  const limparSelecaoImagem = () => {
    setImagem(null);
    if (inputImagemRef.current) inputImagemRef.current.value = '';
  };

  const selecionarImagem = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0] || null;
    evento.target.value = '';
    const erroImagem = obterErroImagem(arquivo);
    if (erroImagem) {
      setErro(erroImagem);
      setMensagem(null);
      setImagem(null);
      return;
    }
    setErro(null);
    setImagem(arquivo);
  };

  const salvar = async () => {
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    try {
      setSalvando(true);
      setErro(null);
      setMensagem(null);
      const imagemUpload = await arquivoParaImagemUpload(imagem);
      if (!montadoRef.current) return;
      const payload: any = {
        telefone: form.telefone,
        whatsapp: form.whatsapp,
        email_operacional: form.email_operacional,
        nif: form.nif,
        provincia: form.provincia,
        municipio: form.municipio,
        endereco: form.endereco,
        referencia: form.referencia,
        contacto_responsavel: form.contacto_responsavel,
        ...(imagemUpload ? { imagem: imagemUpload } : {})
      };
      const resposta = await api.put(endpoint, payload);
      if (!montadoRef.current) return;
      setMensagem(resposta.data.mensagem || 'Perfil atualizado.');
      setStatus(resposta.data.status_perfil || null);
      limparSelecaoImagem();
      window.dispatchEvent(new Event('perfil-operacional-atualizado'));
      await carregar();
    } catch (falha: any) {
      if (montadoRef.current) setErro(extrairMensagemErro(falha, 'Erro ao salvar perfil operacional.'));
    } finally {
      if (montadoRef.current) setSalvando(false);
    }
  };

  const solicitarEliminacao = async () => {
    const confirmou = await dialog.confirm(
      'Deseja solicitar a eliminação da sua conta? Os seus dados pessoais serão removidos, mas registos operacionais obrigatórios serão preservados para fins de auditoria e conformidade fiscal.',
      'Solicitar Eliminação de Dados'
    );

    if (!confirmou) return;

    try {
      setSolicitandoEliminacao(true);
      setErro(null);
      setMensagem(null);
      const resposta = await api.post('/account/deletion-request');
      setMensagem(resposta.data.mensagem);
    } catch (falha: any) {
      setErro(extrairMensagemErro(falha, 'Erro ao solicitar eliminação de conta.'));
    } finally {
      setSolicitandoEliminacao(false);
    }
  };

  return (
    <div className="workspace-page perfil-config-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">{tipo === 'tenant' ? 'Gerente da empresa' : 'Fornecedor'}</span>
          <h1>{tipo === 'tenant' ? 'Configurações da empresa' : 'Configurações do perfil'}</h1>
          <p>Dados operacionais de identificação, contactos e localização usados nos fluxos da plataforma.</p>
        </div>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {carregando && <div className="inline-loading">A carregar perfil...</div>}
      {status && !status.completo && (
        <div className="alerta alerta-aviso">Campos em falta: {status.campos_em_falta.join(', ')}.</div>
      )}
      {status?.completo && <div className="alerta alerta-sucesso">Perfil operacional completo.</div>}

      <section className="panel perfil-form-panel">
        <div className="perfil-image-field">
          {imagemPreviaUrl || imagemUrl ? (
            <img src={imagemPreviaUrl || imagemUrl} alt="Pré-visualização do perfil" />
          ) : (
            <div className="perfil-image-placeholder">Sem imagem</div>
          )}
          <label className="botao" htmlFor="perfil-imagem">
            <Upload size={16} aria-hidden="true" />
            Foto/logotipo
          </label>
          <input
            ref={inputImagemRef}
            id="perfil-imagem"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            type="file"
            onChange={selecionarImagem}
          />
          {imagem && (
            <button className="botao" type="button" onClick={limparSelecaoImagem}>
              <Trash2 size={16} aria-hidden="true" />
              Remover seleção
            </button>
          )}
        </div>

        <div className="perfil-form-grid">
          <label>Telefone *<input value={form.telefone} onChange={(e) => atualizar('telefone', e.target.value)} /></label>
          <label>WhatsApp *<input value={form.whatsapp} onChange={(e) => atualizar('whatsapp', e.target.value)} /></label>
          <label>E-mail operacional *<input type="email" value={form.email_operacional} onChange={(e) => atualizar('email_operacional', e.target.value)} /></label>
          <label>NIF *<input value={form.nif} disabled={tipo === 'tenant'} onChange={(e) => atualizar('nif', e.target.value)} /></label>
          <label>Província *<input value={form.provincia} onChange={(e) => atualizar('provincia', e.target.value)} /></label>
          <label>Município *<input value={form.municipio} onChange={(e) => atualizar('municipio', e.target.value)} /></label>
          <label>Contacto responsável *<input value={form.contacto_responsavel} onChange={(e) => atualizar('contacto_responsavel', e.target.value)} /></label>
          <label className="span-2">Endereço/localização física *<textarea value={form.endereco} onChange={(e) => atualizar('endereco', e.target.value)} /></label>
          <label className="span-2">Referência<input value={form.referencia} placeholder="Próximo ao Banco BIC" onChange={(e) => atualizar('referencia', e.target.value)} /></label>
        </div>

        <div className="form-actions">
          <button className="botao botao-primario" type="button" onClick={salvar} disabled={salvando}>
            <Save size={16} aria-hidden="true" />
            {salvando ? 'A salvar...' : 'Salvar perfil'}
          </button>
        </div>
      </section>

      <section className="panel panel-danger perfil-account-deletion">
        <div className="panel-header">
          <UserX size={20} aria-hidden="true" />
          <h2>Gestão de Dados e Conta</h2>
        </div>
        <p>Em conformidade com a política de privacidade, pode solicitar a remoção dos seus dados identificáveis. Informações relativas a processos de compra concluídos serão mantidas por requisitos legais e de integridade da plataforma.</p>
        <div className="form-actions">
          <button
            className="botao botao-perigo"
            type="button"
            onClick={solicitarEliminacao}
            disabled={solicitandoEliminacao}
          >
            <Trash2 size={16} aria-hidden="true" />
            {solicitandoEliminacao ? 'A processar...' : 'Solicitar eliminação de conta'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default PerfilOperacional;
